import { EventEmitter, Injectable, Output } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { GoogleAnalyticsService } from 'ngx-google-analytics';
import { ToastrService } from 'ngx-toastr';
import { NgxUiLoaderService } from 'ngx-ui-loader';
import { Subject } from 'rxjs';
import { environment } from '../../environments/environment';
import { InspirationRoomService } from '../canvas/services/inspiration-rooms/inspiration-room.service';
import { OptionSelectorService } from '../canvas/services/option-selector.service';
import { NotificationComponent } from '../components/notification-component/notification.component';
import { Appointment, Area, CFAOptions, Option, Tag, TutorialVideo } from '../engine/models/models';
import { OptionEngine } from '../engine/option.engine';
import { SessionType, UserType } from '../enum';
import { Value } from '../models/BingImageResult';
import { InspirationRoomModel } from '../models/InspirationRoomModel';
import { InspirationRoomOptionModel, InspirationRoomOptionStyle } from '../models/InspirationRoomOptionModel';
import { InspirationRoomTipsModel } from '../models/InspirationRoomTipsModel';
import { RequestAssistanceModal } from '../models/requestAssistanceModel';
import { AreasService } from './areas.service';
import { ClientConfiguratioService } from './client-configuration.service';
import { ContractService } from './contract.service';
import { CustomFeaturesAddendumService } from './custom-features-addendum.service';
import { MediaService } from './media.service';
import { OptionTagsService } from './optionTags.service';
import { CollectionsService } from './collection.service';

@Injectable()
export class Globals {
  static engine: any;
  constructor(private contractService: ContractService,
    private clientConfiguration: ClientConfiguratioService,
    private optionTagsService: OptionTagsService,
    private collectionsService: CollectionsService,
    private inspirationRoomService: InspirationRoomService,
    private mediaService: MediaService,
    public optionsService: OptionSelectorService,
    public dialog: MatDialog,
    private loader: NgxUiLoaderService,
    private toastr: ToastrService,
    private areasService: AreasService,
    private router: Router,
    private customFeaturesAddendumService: CustomFeaturesAddendumService,
    private snackBar: MatSnackBar,
    private $gaService: GoogleAnalyticsService
  ) {
  }

  // Google Analytics
  navigateVirtualPage(name: string) {
    this.$gaService.pageView(`/areas/${name}`, name, undefined, {
      contract_id: this.engine.appointmentDetails.contractId,
      contract_lot: this.engine.appointmentDetails.lotId,
      contract_community: this.engine.appointmentDetails.communityCode
    })

    //this.$gaService.pageView('/selections', name);
  }

  viewOption = new Subject<Option>();

  @Output() areaSelectionChanged = new EventEmitter();

  debugMode = false;
  allRooms: InspirationRoomModel[] = [];
  tutorialVideos: TutorialVideo[];
  selectedVideo: TutorialVideo;
  mediaAssociations: Value[];
  hasBackgroundHeaderImage = false;
  showRoomGroupings = true; // For first load, it should be true.
  selectedOption: Option;
  selectedOtherArea: string;
  selectedArea: Area;
  selectedTag: Tag;
  get isDebugMode(): boolean {
    return this.debugMode;
  }

  get selectedAreaName(): string {
    return this.selectedArea ? this.selectedArea.name : this.selectedOtherArea;
  }

  getRequestAssistanceDetails(): RequestAssistanceModal {
    let model: RequestAssistanceModal = {
      area: this.selectedAreaName,
      subArea: this.selectedTag?.name,
      clientEmail: this.appointment.clientEmail,
      clientName: this.appointment.clientName,
      coClientEmail: this.appointment.coClientEmail,
      coClientName: this.appointment.coClientName,
      showroomConsultant: this.appointment.showroomConsultant,
      showroomConsultantEmail: this.appointment.showroomConsultantEmail,
      contract: `${this.appointment.communityCode} / ${this.appointment.lotId}`,
      option: this.selectedOption?.description,
      optionId: this.selectedOption?.optionId,
      loginBy: this.appointment.loginBy,
      message: null
    };
    return model;
  }

  showNotificationPanel(showForcefully = false) {
    this.snackBar.dismiss();
    if (this.selectedArea && this.selectedArea.areaHelpText) {
      if ((this.selectedArea.helpTextHidden || this.isImpersonating) && !showForcefully) {
        return;
      }
      this.snackBar.openFromComponent(NotificationComponent, {
        data: this.selectedArea,
        duration: 60000
      });
    }
  }
  toggleClientAccess() {
    let appointment = this.appointment;
    appointment.isClientLoginBlocked = !appointment.isClientLoginBlocked;
    this.appointment = appointment;
    this.clientConfiguration.toggleClientAccess(this.appointment.contractId).subscribe(response => {
    })
  }
  toggleDebugMode() {
    this.debugMode = !this.debugMode;
  }

  collectionColor: string;
  getCollectionThemes() {
    this.collectionsService.getCollectionTheme().subscribe(res => {
      localStorage.setItem("CollectionTheme", JSON.stringify(res));
    });
  }
  get getCollectionColor(): string {
    if (!localStorage.getItem("CollectionTheme")) {
      this.getCollectionThemes();
    }
    else {
      let collectionTheme = JSON.parse(localStorage.getItem("CollectionTheme"));
      this.collectionColor = collectionTheme.find(x => x.collectionId == this.appointment.collectionId).htmlColorCode;
    }

    return this.collectionColor;
  }

  previousSelectedArea: number;
  previousSelectedOption: Option;
  syncingFailure: boolean;
  areas: Area[];
  cfaOptions: CFAOptions[];
  isBreadCrumb: boolean;
  selectedIndex: number;
  loginFromDS = false;
  hideHeader: boolean;
  isUploading: boolean;
  insperationalRoomId: number;
  subLabel: string;
  currentOptionId: number;
  currentTipId: number;
  defaultTip: boolean;
  selectedCode: string;
  selectedGlobalOption: Option;
  selectedRoomOptionStyles: InspirationRoomOptionStyle[];
  selectedRoomOption: InspirationRoomOptionModel;
  selectedGlobalTip: InspirationRoomTipsModel;
  addedOptions: InspirationRoomOptionModel[];
  addedTips: InspirationRoomTipsModel[];
  engine: OptionEngine;
  globalOptions: Option[];
  globalTips: InspirationRoomTipsModel[];
  hideBreadCrumbs: boolean;
  syncingOptions = false;
  lastSaveCompletedOn: Date;
  roomGroupId: number;
  viewOptionFromCart: boolean = false;

  public backgroundHeaderImage = '/assets/images/potspans.png';
  public displayRooms = true;
  public displayCompleted = false;

  get formattedBackgroundHeaderImage(): string {
    return `url(${this.backgroundHeaderImage})`;
  }

  private _appointment: Appointment;
  private _isImpersonating: boolean;
  private _isReadOnly: boolean;
  set appointment(val: Appointment) {
    this._appointment = val;
    localStorage.setItem("appointment", JSON.stringify(val));
  }

  get appointment(): Appointment {
    if (!this._appointment && localStorage.getItem("appointment")) {
      this._appointment = JSON.parse(localStorage.getItem('appointment'));
    }
    return this._appointment;
  }

  set isImpersonating(val: boolean) {
    this._isImpersonating = val;
    localStorage.setItem("isImpersonating", JSON.stringify(val));
  }
  get isImpersonating(): boolean {
    if (!this._isImpersonating && localStorage.getItem("isImpersonating")) {
      this._isImpersonating = JSON.parse(localStorage.getItem('isImpersonating'));
    }
    return this._isImpersonating;
  }

  set isReadOnly(val: boolean) {
    this._isReadOnly = val;
    localStorage.setItem("isReadOnly", JSON.stringify(val));
  }
  get isReadOnly(): boolean {
    if (!this._isReadOnly && localStorage.getItem("isReadOnly")) {
      this._isReadOnly = JSON.parse(localStorage.getItem('isReadOnly'));
    }
    return this._isReadOnly;
  }

  get isUserAdmin(): boolean {
    const user = JSON.parse(localStorage.getItem("userType"));
    if (user?.isAdmin) {
      return user.isAdmin;
    }
    return false;
  }

  getMediaAssociations() {
    this.mediaService.getMediaAssociations().subscribe(assc => {
      this.mediaAssociations = assc;
    });
  }

  clearAppointment() {
    if (this._appointment) {
      this._appointment = null;
    }
  }

  refreshOptionTags() {
    this.loader.start();
    this.optionTagsService.refreshOptionTags().subscribe(res => {
      this.initializeOptionEngine(false, false);
      this.loader.stop();
    });
  }

  gettingGlobalOption = false;
  getGlobalOptions(isRefresh: boolean) {
    return new Promise<Option[]>(res => {
      if ((isRefresh || !this.globalOptions)) {
        if (!this.gettingGlobalOption || isRefresh) {
          this.loader.start();
          this.gettingGlobalOption = true;
          this.optionsService.getGlobalOptions(isRefresh).subscribe(response => {
            this.globalOptions = response;
            this.gettingGlobalOption = false;
            this.loader.stop();
            res(this.globalOptions);
          }, error => { this.gettingGlobalOption = false; });
        } else {
          res(this.globalOptions);
        }
      }
      else {
        res(this.globalOptions);
      }
    })
  }

  validateSession(forceOptionRefresh = false, isCFA = false) {
    const user = JSON.parse(localStorage.getItem("userType"));

    // 1. Has the client successfully authenticated?
    if (!this.appointment || user.isAdmin) {
      this.clear();
      this.router.navigate(["/login"]);
    }

    // 2. Is the option engine loaded?
    if (!this.engine || forceOptionRefresh) {
      this.initializeOptionEngine(forceOptionRefresh, false);
    }

    if (isCFA) {
      if (!this.engine.cfaOptions) {
        this.getAllSubArea();
        this.getUniqueIds(this.engine);
      }
      return this.engine;
    }
  }

  subAreas = [];

  getAllSubArea() {
    if (this.areas && this.areas.length > 0) {
      for (const area of this.areas) {
        if (area.subAreas && area.subAreas.length > 0) {
          for (const childArea of area.subAreas) {
            let subArea: any = childArea;
            let parentArea = {
              id: area.id,
              name: area.name,
              displayOrder: area.displayOrder
            }
            subArea.parentArea = parentArea
            this.subAreas.push(subArea);
          }
        }
      }
    }
  }

  getClientAreaBySubAreaId(subAreaId: number) {
    for (const area of this.areas) {
      if (area.subAreas?.length) {
        for (const subArea of area.subAreas) {
          if (subArea.id === subAreaId) {
            return area;
          }
        }
      }
    }
  }

  getUniqueIds(engine: OptionEngine) {
    if (engine && engine.appointmentDetails) {
      const areaIds = engine.appointmentDetails.options.map(({ areaId }) => areaId);
      const distinctAreaIds = [];

      // GBJ: Use ES6 loops.  e.g. for(const val of areaIds)
      // Or even better, just use .filter

      areaIds.forEach((val) => {
        if (distinctAreaIds.length === 0) {
          distinctAreaIds.push(val);
        }
        else {
          const _item = distinctAreaIds.find(x => x === val);
          if (!_item) {
            distinctAreaIds.push(val);
          }
        }
      });

      this.getOptionsByGroupByAreaId(engine, distinctAreaIds);
    }
  }

  getOptionsAndChildOptions(items, val) {
    if (items && items.length > 0) {
      if (this.cfaOptions && this.cfaOptions.length > 0) {
        const checkForAddedArea = this.cfaOptions.find(a => a.areaId === val);
        if (checkForAddedArea) {
          for (const item of items) {
            checkForAddedArea.options.push(item);
          }
          return;
        }
      }

      if (this.subAreas && this.subAreas.length > 0) {
        const area = this.subAreas.find(a => a.id === val);

        if (area) {
          this.cfaOptions.push({ areaId: val, areaName: area.name, displayOrder: area.displayOrder, parentDisplayOrder: area.parentArea.displayOrder, options: items });
        }
        else {
          this.cfaOptions.push({ areaId: val, areaName: "N/A", displayOrder: 1, parentDisplayOrder: 0, options: items });
        }
      }
      else {
        this.cfaOptions.push({ areaId: val, areaName: "N/A", displayOrder: 1, parentDisplayOrder: 0, options: items });
      }
    }
  }

  getOptionsByGroupByAreaId(engine: OptionEngine, distinctAreaIds) {
    this.cfaOptions = [];
    distinctAreaIds.forEach((val) => {
      // not Showing structurual area options in cfa
      if (val !== 1 && val !== 2 && val !== 42) {
        const items = engine.appointmentDetails.options.filter(x => x.childOptions && x.isSelected && x.areaId === val);
        if (items && items.length > 0) {
          this.getOptionsAndChildOptions(items, val);
        }

        const optionsWithChildOptions = engine.appointmentDetails.options.filter(x => x.childOptions && x.childOptions.length > 0 && x.areaId === val);

        if (optionsWithChildOptions.length > 0) {
          for (const optionWithChildOptions of optionsWithChildOptions) {
            if (optionWithChildOptions.childOptions && optionWithChildOptions.childOptions.length > 0) {
              const selectedChildOptions = optionWithChildOptions.childOptions.filter(x => x.isSelected);
              if (selectedChildOptions && selectedChildOptions.length > 0) {
                this.getOptionsAndChildOptions(selectedChildOptions, val);
              }
            }
          }
        }
      }
    });
    this.engine.cfaOptions = this.cfaOptions.sort((a, b) => (a.parentDisplayOrder - b.parentDisplayOrder) || (a.displayOrder - b.displayOrder) || a.areaName.localeCompare(b.areaName));
    //this.engine.cfaOptions = this.cfaOptions.sort((a, b) => (a.displayOrder > b.displayOrder) ? 1 : -1);
  }

  clear() {
    let userEmail = localStorage.getItem("userEmail");
    sessionStorage.clear();
    localStorage.clear();
    this.clearAppointment();
    localStorage.setItem("userEmail", userEmail);
  }

  initializeOptionEngine(forceOptionRefresh = false, isRefreshCache: boolean) {
    // The force refresh flag is an administrative feature used for testing.
    // isRefreshCache is used for refreshing cache.it is used when we update json.
    if (forceOptionRefresh && this.engine || isRefreshCache) {
      this.engine = null;
    }

    if (this.appointment && !this.engine) {
      this.syncingFailure = false;

      if ((this.appointment && !this.engine) || isRefreshCache) {
        this.syncingFailure = false;
        this.syncingOptions = true;

        // Load all Media Associations (should wait)
        this.getMediaAssociations();

        const loggedInUser = this.appointment.loginBy == UserType.Client ? this.appointment.clientEmail : this.appointment.coClientEmail;

        this.contractService.getAppointmentWithOptions(this.appointment.contractId,
          loggedInUser,
          forceOptionRefresh,
          isRefreshCache).subscribe(data => {
            this.engine = new OptionEngine(this.$gaService);
            this.engine.mediaAssociations = this.mediaAssociations;
            this.engine.appointmentDetails = data;

            //set last json save/updated date
            if (data.lastUpdatedDate) {
              let appointment = this.appointment;
              this.lastSaveCompletedOn = appointment.lastUpdatedDate = data.lastUpdatedDate;
              this.appointment = appointment;
            }

            if (data.tutorialVideos) {
              let appointment = this.appointment;
              this.tutorialVideos = appointment.tutorialVideos = data.tutorialVideos;
              this.appointment = appointment;
            }

            if (this.engine.appointmentDetails.pricing && this.engine.appointmentDetails.pricing) {
              this.engine.appointmentDetails.pricing.optionsTotal = 0;
            }

            this.inspirationRoomService.getTips().subscribe(response => {
              this.engine.tips = response;
            });

            this.optionTagsService.getOptionTags().subscribe(response => {
              this.engine.loadOptionTags(response);
            });

            this.loadTagFilters(false);

            this.optionTagsService.getTagGroups().subscribe(response => {
              this.engine.tagGroups = response;
            })

            const success = this.engine.loadOptionSchema(data?.options);

            // Fix for exclusion of max pre-qual
            if (this.engine.appointmentDetails.maxPrequalAmount && !this.engine.appointmentDetails.pricing.maxApprovedPrice) {
              // Remove all characters except digits and decimal points
              const valueWithoutSymbols = this.engine.appointmentDetails.maxPrequalAmount.replace(/[^\d.]/g, '');
              this.engine.appointmentDetails.pricing.maxApprovedPrice = parseFloat(valueWithoutSymbols);
            }

            this.syncingOptions = false;

            if (!success) {
              this.syncingFailure = true;
            }

            this.loader.stop();
          }, error => {
            this.toastr.error("Syncing failed.  Please try again.", "Error"),
              this.syncingFailure = true;
            this.syncingOptions = false;
            this.loader.stop();
          });
      }
    }
  }

  loadTagFilters(isRefresh = false) {
    if (isRefresh) {
      this.loader.start();
    }
    this.optionTagsService.getTagFilters(isRefresh).subscribe(response => {
      this.engine.tagFilter = response;
      this.loader.stop();
    })
  }

  downloadCFAByEmailAndContractId(contractId: number, email: string) {
    this.loader.start();
    this.customFeaturesAddendumService.downloadCFAByEmailAndContractId(contractId, email).subscribe(data => {
      //const detailedReport: any = record;
      const linkSource = 'data:application/pdf;base64,' + data;
      const downloadLink = document.createElement("a");
      downloadLink.href = linkSource;
      downloadLink.download = `${contractId}_True_Style_Guide_Selections.pdf`
      downloadLink.click();
      this.loader.stop();
    }, error => {
      this.toastr.error("An unknown error occurred.", "Error"),
        this.loader.stop();
    });
  }


  downloadCFA(appointment) {
    this.loader.start();

    this.customFeaturesAddendumService.downloadCFA(appointment).subscribe(data => {
      //const detailedReport: any = record;
      const linkSource = 'data:application/pdf;base64,' + data;
      const downloadLink = document.createElement("a");
      downloadLink.href = linkSource;
      downloadLink.download = `${appointment.contractId}_True_Style_Guide_Selections.pdf`
      downloadLink.click();
      this.loader.stop();
    }, error => {
      this.toastr.error("An unknown error occurred.", "Error"),
        this.loader.stop();
    });
  }

  exportFavoritedOptions(contractId: number, email: string) {
    this.loader.start();

    let likedOptions = this.getLikedOptionIds(this.engine.displayedOptions);

    let model = {
      ContractId: contractId,
      Email: email
    }

    this.customFeaturesAddendumService.downloadfavorited(model).subscribe(record => {
      const detailedReport: any = record;
      const linkSource = 'data:application/pdf;base64,' + detailedReport;
      const downloadLink = document.createElement("a");
      downloadLink.href = linkSource;
      downloadLink.download = "ClientExperienceReport.pdf";
      downloadLink.click();
      this.loader.stop();
    }, error => {
      let msg = error;
      this.toastr.error("An unknown error occurred.", "Error"),
        this.loader.stop();
    });
  }

  getLikedOptionIds(options: Option[]) {
    let optionIds: number[] = [];

    for (let option of options) {
      if (option.isLiked) {
        optionIds.push(option.optionId);
        continue;
      }

      if (option.childOptions?.length) {
        for (let child of option.childOptions) {
          if (child.isLiked) {
            optionIds.push(child.optionId);
          }
        }
      }
    }

    return optionIds;
  }

  initAutoSave() {
    if (this.lastSaveCompletedOn) {
      let nowDate: any = new Date();
      let lastSavedDate: any = new Date(this.lastSaveCompletedOn);
      let diffMins: number = Math.round(((nowDate - lastSavedDate)) / 60000);
      if (diffMins > 5 && !this.engine?.isSaving) {
        this.saveDataIfApplicable();
      }
    }
    setTimeout(() => this.initAutoSave(), 10000);
  }

  saveDataIfApplicable() {
    if (this?.engine?.isDirty && !this.isImpersonating) {
      this.updateOptionsJson()
    }
  }

  updateOptionsJson() {
    const getCircularReplacer = () => {
      const seen = new WeakSet()
      return (key, value) => {
        if (typeof value === "object" && value !== null) {
          if (seen.has(value)) return
          seen.add(value)
        }
        return value
      }
    }

    this.syncingOptions = true;
    this.engine.isSaving = true;


    const appointment: Appointment = JSON.parse(JSON.stringify(this.appointment, getCircularReplacer()));

    const options: Option[] = JSON.parse(JSON.stringify(this.engine.optionDefinitions, getCircularReplacer()));

    // Ensure any options that are hidden don't show up as added
    for (const option of options) {
      if (option.areaId > 1) {
        if (option.childOptions) {

          for (const childOption of option.childOptions) {
            if (childOption.isSelected || childOption.isAdded) {
              if (!option.isVisible || !childOption.isVisible) {
                childOption.isSelected = false;
                childOption.isAdded = false;
              }
            }
          }
        } else {
          if ((option.isSelected || option.isAdded) && !option.isVisible) {
            option.isSelected = false;
            option.isAdded = false;
          }
        }
      }
    }

    appointment.options = this.removeUnwantedProperties(options);
    appointment.pricing = this.engine.appointmentDetails.pricing;
    this.inspirationRoomService.updateJson(appointment).subscribe(data => {

      //set last json save/updated date
      if (data.lastUpdatedDate) {
        let appointment = this.appointment;
        this.lastSaveCompletedOn = appointment.lastUpdatedDate = new Date(data.lastUpdatedDate);
        this.appointment = appointment;
      }

      this.engine.isSaving = false;
      this.syncingOptions = false;

      //still dirty because the user done some changes while saving is in progress.
      if (this.engine.isDirtyWhileSaving) {
        this.engine.isDirty = true;
        this.engine.isDirtyWhileSaving = false;
      } else {
        this.engine.isDirty = false;
      }
    });
  }

  removeUnwantedProperties(options: Option[]): Option[] {
    options.forEach((option) => {
      this.deleteProperty(option);
      if (option.childOptions) {
        option.childOptions.forEach((childOption) => {
          this.deleteProperty(childOption);
        });
      }
    });
    return options;
  }

  deleteProperty(option: Option) {
    if (option.pictureUrl && option.pictureUrl.includes(environment.imageBaseUrl)) {
      option.pictureUrl = option.pictureUrl.replace(environment.imageBaseUrl, '');
      option.pictureUrl = decodeURIComponent(option.pictureUrl);
    }
    if (option.defaultGalleryPictureMissing) {
      delete (option.pictureUrl);
    }
    if (option.hasOwnProperty("prepared")) {
      delete (option.prepared);
    }
    if (option.hasOwnProperty("selectedOption")) {
      delete (option.selectedOption);
    }
    if (option.hasOwnProperty("selectedPictureGallery")) {
      delete (option.selectedPictureGallery);
    }
    if (option.hasOwnProperty("isVisible")) {
      delete (option.isVisible);
    }
    if (option.hasOwnProperty("selectionRequired")) {
      delete (option.selectionRequired);
    }
  }

  getTutorialVideos() {
    this.mediaService.getTutorialVideos().subscribe(response => {
      this.tutorialVideos = response;
    });
  }

  UpdateClientSession() {
    if (localStorage.getItem('appointment')) {
      if (!this.isImpersonating) {
        this.clientConfiguration.UpdateClientSession(this.appointment.contractId, SessionType.Update).subscribe();
        //setTimeout(() => this.UpdateClientSession(), (60000 * 9));
      }
    }
  }

  viewSelectedOption(option: Option) {
    this.viewOption.next(option);
  }

  getAllGobalOption(): Option[] {
    let options: Option[] = [];
    if (this.globalOptions && this.globalOptions.length) {
      options = this.globalOptions.filter(q => q.childOptions?.length).flatMap(a => a.childOptions);
      options = this.globalOptions.concat(options);
      return options;
    } else {
      this.getGlobalOptions(false);
      return options;
    }
  }
}
